import React, { memo, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import FeatureCard from './FeatureCard';
import FeatureCardSkeleton from './FeatureCardSkeleton';
import { TabContentProps } from './types';
import styles from './TabContent.module.css';

/**
 * SubclassTab - Displays subclass features and multiclass foundations in progression order
 * Optimized with React.memo and useMemo for feature sorting
 */
const SubclassTab: React.FC<TabContentProps> = memo(({
  features,
  expandedFeatures,
  onToggleExpand,
  isLoading = false
}) => {
  // Sort features by progression: foundation → specialization → mastery → multiclass-foundation
  // Memoize to avoid re-sorting on every render
  // Must be called before any early returns (hooks rules)
  const sortedFeatures = useMemo(() => {
    if (features.length === 0) return [];
    return [...features].sort((a, b) => {
      const order = ['subclass-foundation', 'subclass-specialization', 'subclass-mastery', 'multiclass-foundation'];
      return order.indexOf(a.type) - order.indexOf(b.type);
    });
  }, [features]);

  if (isLoading && features.length === 0) {
    return (
      <div className={styles.tabContent}>
        <FeatureCardSkeleton count={1} />
      </div>
    );
  }

  if (features.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>No subclass selected yet.</p>
        <p className={styles.emptyHint}>Choose a subclass during character creation or level-up.</p>
      </div>
    );
  }

  return (
    <div className={styles.tabContent}>
      <AnimatePresence mode="popLayout">
        {sortedFeatures.map((feature) => (
          <FeatureCard
            key={feature.id}
            feature={feature}
            isExpanded={expandedFeatures.has(feature.id)}
            onToggleExpand={() => onToggleExpand(feature.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );
});

SubclassTab.displayName = 'SubclassTab';

export default SubclassTab;
